import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  CircularProgress,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';

import StandingsWidget from '../../components/stats/StandingsWidget';
import {
  getCompetitionsForRankingBuilder,
  getCompetitionSeasonsForRankingBuilder,
  getRankingCompetitionMeta,
} from '../../../api';

const DISCIPLINE_LABELS = {
  rugby: 'Rugby',
  soccer: 'Football',
};

const DISCIPLINE_OPTIONS = [
  { value: 'rugby', label: 'Rugby' },
  { value: 'soccer', label: 'Football' },
];

const getSeasonDisplayLabel = (seasonValue) => {
  const value = String(seasonValue || '').trim();
  if (!value) return '';

  if (value.includes('/')) {
    return value.split('/')[0];
  }

  return value;
};

const normalizeSeasonOptions = (rawSeasons = []) => {
  const byLabel = new Map();

  rawSeasons.forEach((seasonValue) => {
    const value = String(seasonValue || '').trim();
    if (!value) return;

    const label = getSeasonDisplayLabel(value);
    const existing = byLabel.get(label);

    if (!existing) {
      byLabel.set(label, { value, label });
      return;
    }

    if (!existing.value.includes('/') && value.includes('/')) {
      byLabel.set(label, { value, label });
    }
  });

  return Array.from(byLabel.values()).sort((a, b) =>
    b.label.localeCompare(a.label)
  );
};

const parsePositiveDay = (value) => {
  if (value == null || value === '') return '';
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed < 1) return '';
  return String(Math.floor(parsed));
};

export default function StandingsBuilderPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const provider = searchParams.get('provider') || '';
  const discipline = searchParams.get('discipline') || '';
  const seasonParam = searchParams.get('season') || '';
  const seasonSinceParam = searchParams.get('seasonSince') || '';
  const competitionIdsParam = searchParams.get('competitionIds') || '';
  const dayParam = searchParams.get('day') || '';

  

  const selectedCompetitionIds = useMemo(
    () =>
      competitionIdsParam
        .split(',')
        .map((id) => id.trim())
        .filter(Boolean),
    [competitionIdsParam]
  );

  const selectedSeasonsFromUrl = useMemo(
    () =>
      seasonParam
        .split(',')
        .map((value) => value.trim())
        .filter(Boolean),
    [seasonParam]
  );

  const widgetSeason =
    selectedSeasonsFromUrl.length > 0
      ? selectedSeasonsFromUrl.join(',')
      : null;

 const isMultiCompetition = selectedCompetitionIds.length > 1;

  const [seasonOptions, setSeasonOptions] = useState([]);
  const [selectedSeasons, setSelectedSeasons] = useState([]);
  const [loadingSeasons, setLoadingSeasons] = useState(false);

  const [competitions, setCompetitions] = useState([]);
  const [selectedCompetitions, setSelectedCompetitions] = useState([]);
  const [loadingCompetitions, setLoadingCompetitions] = useState(false);
  const [competitionsError, setCompetitionsError] = useState('');

  const [title, setTitle] = useState('Classement');
  const [metaLoading, setMetaLoading] = useState(false);
  const [dayInput, setDayInput] = useState(parsePositiveDay(dayParam));
  const [maxDay, setMaxDay] = useState(null);

  useEffect(() => {
    setDayInput(parsePositiveDay(dayParam));
  }, [dayParam]);

  useEffect(() => {
    if (discipline) return;

    const nextParams = new URLSearchParams(searchParams);
    nextParams.set('discipline', 'rugby');
    setSearchParams(nextParams, { replace: true });
  }, [discipline, searchParams, setSearchParams]);

  useEffect(() => {
    let ignore = false;

    const loadSeasons = async () => {
      if (!provider || !discipline) {
        setSeasonOptions([]);
        setSelectedSeasons([]);
        return;
      }

      try {
        setLoadingSeasons(true);

        const { data } = await getCompetitionSeasonsForRankingBuilder({
          provider,
          discipline,
        });

        if (ignore) return;

        const rawSeasons = Array.isArray(data) ? data : [];
        const normalizedSeasons = normalizeSeasonOptions(rawSeasons);

        setSeasonOptions(normalizedSeasons);

        if (selectedSeasonsFromUrl.length) {
          setSelectedSeasons(
            normalizedSeasons.filter((option) =>
              selectedSeasonsFromUrl.includes(option.value)
            )
          );
        } else if (!seasonSinceParam && normalizedSeasons.length) {
          const latestSeason = normalizedSeasons[0];
          setSelectedSeasons([latestSeason]);

          const nextParams = new URLSearchParams(searchParams);
          nextParams.set('season', latestSeason.value);
          setSearchParams(nextParams, { replace: true });
        }
      } catch (error) {
        if (!ignore) {
          setSeasonOptions([]);
          setSelectedSeasons([]);
        }
      } finally {
        if (!ignore) {
          setLoadingSeasons(false);
        }
      }
    };

    loadSeasons();

    return () => {
      ignore = true;
    };
  }, [provider, discipline, selectedSeasonsFromUrl, seasonSinceParam]);

  useEffect(() => {
    let ignore = false;

    const loadCompetitions = async () => {
      if (!provider || !discipline) {
        setCompetitions([]);
        setSelectedCompetitions([]);
        return;
      }

      try {
        setLoadingCompetitions(true);
        setCompetitionsError('');

        const { data } = await getCompetitionsForRankingBuilder({
          provider,
          discipline,
          season: selectedSeasons.length
            ? selectedSeasons.map((option) => option.value).join(',')
            : undefined,
          seasonSince: seasonSinceParam || undefined,
        });

        if (ignore) return;

        const normalized = Array.isArray(data) ? data : [];
        setCompetitions(normalized);

        const preselected = normalized.filter((competition) =>
          selectedCompetitionIds.includes(String(competition._id))
        );

        setSelectedCompetitions(preselected);
      } catch (error) {
        if (!ignore) {
          setCompetitions([]);
          setSelectedCompetitions([]);
          setCompetitionsError('Impossible de charger les compétitions.');
        }
      } finally {
        if (!ignore) {
          setLoadingCompetitions(false);
        }
      }
    };

    loadCompetitions();

    return () => {
      ignore = true;
    };
  }, [provider, discipline, selectedSeasons, seasonSinceParam, selectedCompetitionIds]);

  useEffect(() => {
    let ignore = false;

    const loadMeta = async () => {
      const firstCompetitionId = selectedCompetitionIds[0];

      if (!firstCompetitionId) {
        if (!ignore) {
          setTitle('Classement');
          setMetaLoading(false);
          setMaxDay(null);
        }
        return;
      }

      try {
        setMetaLoading(true);

        const { data } = await getRankingCompetitionMeta(firstCompetitionId);
        if (ignore) return;

        const nbDays = Number(data?.nbDays);
        const normalizedMaxDay =
          Number.isFinite(nbDays) && nbDays > 0 ? nbDays : null;

        setTitle(
          selectedCompetitionIds.length > 1
            ? 'Classement cumulé'
            : data?.name || 'Classement'
        );
        setMaxDay(normalizedMaxDay);

        const parsedDay = parsePositiveDay(dayParam);

        if (!parsedDay && normalizedMaxDay) {
          const nextParams = new URLSearchParams(searchParams);
          nextParams.set('day', String(normalizedMaxDay));
          setSearchParams(nextParams, { replace: true });
        } else if (
          parsedDay &&
          normalizedMaxDay &&
          Number(parsedDay) > normalizedMaxDay
        ) {
          const nextParams = new URLSearchParams(searchParams);
          nextParams.set('day', String(normalizedMaxDay));
          setSearchParams(nextParams, { replace: true });
        }
      } catch (error) {
        if (!ignore) {
          setTitle('Classement');
          setMaxDay(null);
        }
      } finally {
        if (!ignore) {
          setMetaLoading(false);
        }
      }
    };

    loadMeta();

    return () => {
      ignore = true;
    };
  }, [selectedCompetitionIds, dayParam, searchParams, setSearchParams]);

  const handleDisciplineChange = (event) => {
    const value = event.target.value;

    const nextParams = new URLSearchParams();
    if (provider) nextParams.set('provider', provider);
    if (value) nextParams.set('discipline', value);

    setSelectedSeasons([]);
    setSelectedCompetitions([]);
    setDayInput('');
    setMaxDay(null);

    setSearchParams(nextParams);
  };

  const handleSeasonsChange = (_, value) => {
    setSelectedSeasons(value);
    setSelectedCompetitions([]);
    setDayInput('');
    setMaxDay(null);

    const nextParams = new URLSearchParams();
    if (provider) nextParams.set('provider', provider);
    if (discipline) nextParams.set('discipline', discipline);

    if (value.length) {
      nextParams.set(
        'season',
        value.map((option) => option.value).join(',')
      );
    }

    if (value.length === 0 && seasonSinceParam) {
      nextParams.set('seasonSince', seasonSinceParam);
    }

    setSearchParams(nextParams);
  };

  const handleCompetitionsChange = (_, value) => {
    setSelectedCompetitions(value);

    const nextParams = new URLSearchParams();
    if (provider) nextParams.set('provider', provider);
    if (discipline) nextParams.set('discipline', discipline);

    if (selectedSeasons.length) {
        nextParams.set(
        'season',
        selectedSeasons.map((option) => option.value).join(',')
        );
    }

    if (!selectedSeasons.length && seasonSinceParam) {
        nextParams.set('seasonSince', seasonSinceParam);
    }

    if (value.length) {
        nextParams.set(
        'competitionIds',
        value.map((competition) => String(competition._id)).join(',')
        );
    }

    // journée seulement en mono-compétition
    if (value.length === 1 && dayParam) {
        nextParams.set('day', dayParam);
    }

    setSearchParams(nextParams);
    };

  const handleDayChange = (event) => {
    const value = event.target.value.replace(/[^\d]/g, '');
    setDayInput(value);
  };

  const handleDayBlur = () => {
    const normalized = parsePositiveDay(dayInput);

    const nextParams = new URLSearchParams(searchParams);

    if (!normalized) {
      nextParams.delete('day');
      setSearchParams(nextParams, { replace: true });
      return;
    }

    let nextDay = Number(normalized);

    if (maxDay && nextDay > maxDay) {
      nextDay = maxDay;
    }

    nextParams.set('day', String(nextDay));
    setSearchParams(nextParams, { replace: true });
  };

  const handleDaySelectChange = (event) => {
    const value = parsePositiveDay(event.target.value);

    const nextParams = new URLSearchParams(searchParams);

    if (!value) {
      nextParams.delete('day');
    } else {
      nextParams.set('day', value);
    }

    setSearchParams(nextParams, { replace: true });
  };

  const currentDay =
    parsePositiveDay(dayParam) || (maxDay ? String(maxDay) : '');

  const dayOptions = useMemo(() => {
    if (!maxDay) return [];
    return Array.from({ length: maxDay }, (_, index) => {
      const value = index + 1;
      return { value: String(value), label: `Journée ${value}` };
    }).reverse();
  }, [maxDay]);

  const canRenderWidget =
    Boolean(discipline) && selectedCompetitionIds.length > 0;

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: '100%',
        boxSizing: 'border-box',
        px: { xs: 1.5, sm: 2, md: 3 },
        py: { xs: 2, md: 3 },
        bgcolor: '#fff',
      }}
    >
      <Box sx={{ maxWidth: 1180, mx: 'auto' }}>
        <Stack spacing={3}>
          <Paper
            elevation={0}
            variant="outlined"
            sx={{
              borderRadius: 3,
              borderColor: '#e5e7eb',
              p: { xs: 2, md: 3 },
            }}
          >
            <Stack spacing={2}>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 800 }}>
                  Classement {isMultiCompetition ? 'combiné' : ''}
                </Typography>
                {discipline ? (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 0.5 }}
                  >
                    {DISCIPLINE_LABELS[discipline] || discipline}
                  </Typography>
                ) : null}
              </Box>

              <TextField
                select
                fullWidth
                label="Sport"
                value={discipline}
                onChange={handleDisciplineChange}
              >
                {DISCIPLINE_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>

              {!seasonSinceParam && (
                <Autocomplete
                  multiple
                  options={seasonOptions}
                  value={selectedSeasons}
                  loading={loadingSeasons}
                  onChange={handleSeasonsChange}
                  getOptionLabel={(option) => option?.label || ''}
                  isOptionEqualToValue={(option, value) =>
                    option.value === value.value
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Saisons"
                      placeholder="Sélectionner une ou plusieurs saisons"
                    />
                  )}
                />
              )}

              <Autocomplete
                multiple
                disableCloseOnSelect
                options={competitions}
                value={selectedCompetitions}
                loading={loadingCompetitions}
                onChange={handleCompetitionsChange}
                getOptionLabel={(option) =>
                  option?.season
                    ? `${option.name} (${option.season})`
                    : option?.name || ''
                }
                isOptionEqualToValue={(option, value) =>
                  String(option._id) === String(value._id)
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Compétitions"
                    placeholder="Sélectionner une ou plusieurs compétitions"
                  />
                )}
              />

              {selectedCompetitionIds.length === 1 ? (
                    maxDay ? (
                        <TextField
                        select
                        fullWidth
                        label="Journée"
                        value={currentDay}
                        onChange={handleDaySelectChange}
                        >
                        {dayOptions.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                            {option.label}
                            </MenuItem>
                        ))}
                        </TextField>
                    ) : (
                        <TextField
                        fullWidth
                        label="Journée"
                        value={dayInput}
                        onChange={handleDayChange}
                        onBlur={handleDayBlur}
                        placeholder="Ex. 18"
                        inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                        />
                    )
                    ) : null}

              {competitionsError ? (
                <Alert severity="error">{competitionsError}</Alert>
              ) : null}
            </Stack>
          </Paper>

          {canRenderWidget ? (
            metaLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={28} />
              </Box>
            ) : (
              <StandingsWidget
                provider={provider}
                discipline={discipline}
                competitionIds={selectedCompetitionIds}
                day={!isMultiCompetition && currentDay ? Number(currentDay) : null}
                title=""
                onNavigate={(url) => navigate(url)}
                />
            )
          ) : null}
        </Stack>
      </Box>
    </Box>
  );
}